import { ref, inject } from 'vue';
import { defineStore } from 'pinia';
import { useStoreRestaurant } from '@/stores/restaurant';

export const useStoreAnalytics = defineStore('analytics', () => {
  const api = inject('api');
  const logger = inject('logger');
  const LOGGER_TAG = 'analytics';

  const config = ref(null);
  const googleAnalyticsLoaded = ref(false);
  const googleAnalyticsEcommerceLoaded = ref(false);
  const googleTagManagerLoaded = ref(false);
  const facebookPixelLoaded = ref(false)

  const rStore = useStoreRestaurant();

  const providers = ref([]);

  /**
    * Funzione helper per caricare uno script esterno.
    * @param {string} src - URL dello script da caricare.
    * @param {boolean} [async=true] - Se caricare lo script in modalità async.
    * @returns {Promise} - Si risolve quando lo script è caricato.
  */
  const loadScript = (src, async = true) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = src
      script.async = async
      script.onload = resolve
      script.onerror = () =>
        reject(new Error(`Errore nel caricamento dello script: ${src}`))
      document.head.appendChild(script)
    })
  }

  /**
    * Inizializza i sistemi di analytics.
    * La configurazione attesa è un oggetto contenente i seguenti campi:
    *  - google_analytics_id
    *  - google_tagm_anager_id
    *  - facebook_pixel_id
    *
    * @param {Object} _config - La configurazione degli analytics.
  */
  const initAnalytics = async (_config) => {
    if (!_config) {
      logger.warn(LOGGER_TAG, 'Configurazione analytics non disponibile.')
      return
    }

    config.value = _config;
    logger.info(LOGGER_TAG, 'Configurazione analytics:', config.value)

    let googleAnalyticsId = config.value.google_analytics_id;
    if (googleAnalyticsId && !googleAnalyticsLoaded.value) {
      try {
        await loadScript(
          `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`
        )
        // Inizializzazione di gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
          dataLayer.push(arguments);
        }
        gtag('js', new Date());
        gtag('config', googleAnalyticsId);
        googleAnalyticsLoaded.value = true;
        providers.value.push('googleAnalytics');
        logger.info(LOGGER_TAG, 'Google Analytics inizializzato')
      } catch (error) {
        logger.error(
          LOGGER_TAG,
          'Errore nel caricamento di Google Analytics:',
          error
        )
      }
    }

    let googleAnalyticsEcommerce = config.value.google_analytics_ec_tracking;
    if (googleAnalyticsId && googleAnalyticsEcommerce && !googleAnalyticsEcommerceLoaded.value) {
      try {
        await loadScript('https://www.google-analytics.com/analytics.js');

        window.GoogleAnalyticsObject = 'ga';
        window.ga = window.ga || function () {
          (window.ga.q = window.ga.q || []).push(arguments);
        };

        ga.l = new Date();
        ga('create', googleAnalyticsId, 'auto');

        googleAnalyticsEcommerceLoaded.value = true;
        logger.info(LOGGER_TAG, 'Google Analytics Ecommerce inizializzato');
      } catch (error) {
        logger.error(LOGGER_TAG, 'Errore nel caricamento di Google Analytics (analytics.js):', error);
      }
    }

    let googleTagManagerId = config.value.google_tag_manager_id
    if (googleTagManagerId && !googleTagManagerLoaded.value) {
      try {
        ; (function (w, d, s, l, i) {
          w[l] = w[l] || []
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
          const f = d.getElementsByTagName(s)[0]
          const j = d.createElement(s)
          const dl = l !== 'dataLayer' ? '&l=' + l : ''
          j.async = true
          j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
          f.parentNode.insertBefore(j, f)
        })(window, document, 'script', 'dataLayer', googleTagManagerId)

        // Inserimento del blocco <noscript> per GTM
        if (!document.getElementById('gtm-noscript')) {
          const noscriptElem = document.createElement('noscript')
          noscriptElem.id = 'gtm-noscript'
          noscriptElem.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
          document.body.insertAdjacentElement('afterbegin', noscriptElem)
        }
        googleTagManagerLoaded.value = true;
        providers.value.push('googleTagManager');
        logger.info(LOGGER_TAG, 'Google Tag Manager inizializzato')
      } catch (error) {
        logger.error(
          LOGGER_TAG,
          'Errore nel caricamento di Google Tag Manager:',
          error
        )
      }
    }

    let facebookPixelId = config.value.facebook_pixel_id;
    if (facebookPixelId && !facebookPixelLoaded.value) {
      try {
        !(function (f, b, e, v, n, t, s) {
          if (f.fbq) return
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
          }
          if (!f._fbq) f._fbq = n
          n.push = n
          n.loaded = true
          n.version = '2.0'
          n.queue = []
          t = b.createElement(e)
          t.async = true
          t.src = v
          s = b.getElementsByTagName(e)[0]
          s.parentNode.insertBefore(t, s)
        })(
          window,
          document,
          'script',
          'https://connect.facebook.net/en_US/fbevents.js'
        )

        // Inserimento del blocco <noscript> per Facebook Pixel
        if (!document.getElementById('fb-pixel-noscript')) {
          const noscriptElemFB = document.createElement('noscript')
          noscriptElemFB.id = 'fb-pixel-noscript'
          noscriptElemFB.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${facebookPixelId}&ev=PageView&noscript=1" />`
          document.body.insertAdjacentElement('afterbegin', noscriptElemFB)
        }

        fbq('init', facebookPixelId)
        fbq('track', 'PageView')

        facebookPixelLoaded.value = true;
        providers.value.push('facebookPixel');
        logger.info(LOGGER_TAG, 'Facebook Pixel inizializzato')
      } catch (error) {
        logger.error(
          LOGGER_TAG,
          'Errore nel caricamento di Facebook Pixel:',
          error
        )
      }
    }
  }

  /**
    * Invia manualmente un evento.
    *
    * @param {string} eventName - Nome dell'evento.
    * @param {Object} [eventParams={}] - Parametri aggiuntivi per l'evento.
    *  @param {string|null} [eventId=null] - ID dell'evento (opzionale).
    * @param {Array<string>} [providers=[]] - Array opzionale di provider per cui inviare l’evento.
    *   Se l'array è vuoto, l'evento verrà inviato a tutti i tracker attivi.
  */
  const triggerEvent = (eventName, eventParams = {}, eventId = null, _providers = []) => {
    // Se non è specificato nessun provider, usiamo tutti quelli disponibili.
    if (_providers.length === 0) {
      _providers = providers.value;
    }

    _providers.forEach((provider) => {
      if (provider === 'googleAnalytics' && googleAnalyticsLoaded.value) {
        if (typeof gtag === 'function') {
          gtag('event', eventName, eventParams)
          logger.info(
            LOGGER_TAG,
            `Evento Google Analytics inviato: ${eventName}`
          )
        } else {
          logger.warn(LOGGER_TAG, 'Google Analytics non inizializzato')
        }
        return;
      }

      if (provider === 'googleTagManager' && googleTagManagerLoaded.value) {
        if (window.dataLayer && window.dataLayer.push) {
          window.dataLayer.push({
            event: eventName,
            ...eventParams
          })
          logger.info(
            LOGGER_TAG,
            `Evento Google Tag Manager inviato: ${eventName}`
          )
        } else {
          logger.warn(LOGGER_TAG, 'Google Tag Manager non inizializzato')
        }
        return
      }

      if (provider === 'facebookPixel' && facebookPixelLoaded.value) {
        // Se fbq è disponibile, usiamo il metodo standard
        if (typeof window.fbq === 'function') {
          if (eventId) {
            window.fbq('track', eventName, eventParams, { eventId })
          } else {
            window.fbq('track', eventName, eventParams);
          }

          logger.info(
            LOGGER_TAG,
            `Evento Facebook Pixel inviato: ${eventName} ${eventId}`
          )
          return;
        } else {
          // Altrimenti, usiamo il fallback noscript creando dinamicamente un'immagine
          if (config.value && config.value.facebookPixelId) {
            const queryParams = new URLSearchParams()
            queryParams.append('id', config.value.facebookPixelId)
            queryParams.append('ev', eventName)
            queryParams.append('noscript', '1')
            // Aggiungiamo eventuali parametri extra come custom data (cd)
            for (const [key, value] of Object.entries(eventParams)) {
              queryParams.append(`cd[${key}]`, value)
            }
            const imgUrl = `https://www.facebook.com/tr?${queryParams.toString()}`
            const img = document.createElement('img')
            img.height = 1
            img.width = 1
            img.style.display = 'none'
            img.src = imgUrl
            document.body.insertAdjacentElement('afterbegin', img)
            logger.info(
              LOGGER_TAG,
              `Fallback (noscript) per evento Facebook Pixel inviato: ${eventName}`
            )
          } else {
            logger.warn(
              LOGGER_TAG,
              'Facebook Pixel non inizializzato e configurazione mancante'
            )
          }
        }
        return
      }

      logger.warn(
        LOGGER_TAG,
        `Provider ${provider} non riconosciuto. Usa 'googleAnalytics', 'googleTagManager' o 'facebookPixel'.`
      )
    })
  }

  /**
    * Traccia una transazione ecommerce utilizzando Google Analytics (analytics.js).
  */
  const trackGAEcommerce = (order) => {
    if (typeof window.ga === 'undefined') {
      logger.warn(LOGGER_TAG, 'Google Analytics (analytics.js) non inizializzato');
      return;
    }

    window.ga('require', 'ecommerce');
    let payload = {
      id: order.id,
      affiliation: rStore.restaurant.name || '',
      revenue: order.total,
      shipping: order.delivery_fee,
      tax: '0',
      currency: order.currency
    };
    window.ga('ecommerce:addTransaction', payload);
    logger.info(LOGGER_TAG, 'trackGAEcommerce | order', payload);

    if (order.details && Array.isArray(order.details)) {
      order.details.forEach(detail => {
        let payload = {
          id: order.id,
          name: detail.product._name,
          sku: detail.product.id,
          category: detail.product.category ? detail.product.category._name : '',
          price: detail.price,
          quantity: detail.quantity,
          currency: order.currency
        };
        logger.info(LOGGER_TAG, 'trackGAEcommerce | detail', payload);
        if (detail.product) {
          window.ga('ecommerce:addItem', payload);
        }
      });
    }

    window.ga('ecommerce:send');
    logger.info(LOGGER_TAG, `Ecommerce tracking inviato per l'ordine ${order.id}`);
  };

  /*
   * Interceptor per le risposte API.
   * Se la risposta contiene eventi di tracciamento, li invia.
   */
  api.interceptors.response.use(response => {
    let events = response.data?.analytics_track_events;
    if (!events) {
      return response;
    }
    events = Array.isArray(events) ? events : [events];
    events.forEach(evt => {
      triggerEvent(
        evt.eventName,
        evt.eventParams,
        evt.eventId,
        evt.providers || [],
      )
    })
    return response
  }, error => Promise.reject(error))

  return {
    config,
    googleAnalyticsLoaded,
    googleTagManagerLoaded,
    facebookPixelLoaded,
    googleAnalyticsEcommerceLoaded,
    initAnalytics,
    triggerEvent,
    trackGAEcommerce
  }
})
